import axios from 'axios';
import { activePartnerAccount } from './partnerAccount';
import { loginPath } from './partnerHost';
import { isCacheable, cacheSet, cacheGet, isNetworkError, replayQueue } from './offlineData';
import { mirrorAuthTokenToSW, clearAuthTokenForSW } from './swAuthBridge';
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  timeout: 20000, // 20s — protects against backend hangs blocking the entire app
});

// ── Bearer fallback, only where the cookie cannot reach ─────────────────────
// Auth is an httpOnly SSO cookie (middleware/auth.js SSO_COOKIE_OPTS: httpOnly,
// secure, SameSite=Lax, domain COOKIE_DOMAIN). JavaScript cannot read it, so an
// XSS cannot steal it. A localStorage JWT has none of that protection: any
// script that runs on the page can read it, and it survives a browser restart.
//
// It exists for deployments the cookie cannot reach — a different registrable
// domain, where the session cookie is simply not sent. That is a real need, but it is
// NOT the need of the deployment almost everyone uses: REACT_APP_API_URL is
// unset in production, so baseURL is '/api', the backend serves the SPA from
// backend/public, and every request is same-origin with the cookie attached.
// The mobile app is the same origin too — capacitor.config.ts loads
// the public app URL rather than a bundled build.
//
// So the token is stored and sent only when this build actually talks to a
// different origin. Everywhere else it is not written, not attached, and any
// copy left by an earlier version is deleted on load.
// INCIDENT 2026-08-26 — reverted to always-on.
//
// Scoping the bearer token to cross-origin builds locked real users out: they
// signed in, the page reloaded, and they landed back on /login. The reasoning
// was that same-origin requests carry the httpOnly cookie, which is true for
// the request I tested — but the token was also masking a cookie that does not
// arrive for every user, and removing it exposed that.
//
// Restoring the previous behaviour first and diagnosing second: people cannot
// work while this is wrong, and a security improvement that logs the company
// out is not an improvement.
export function needsBearerFallback() {
  return true;
}

const TOKEN_KEY = 'aone_token';

/** Persist the JWT only where it is the only way to authenticate. */
export function storeAuthToken(token) {
  if (!token) return;
  if (!needsBearerFallback()) return;            // the cookie already carries it
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* storage full/blocked */ }
  // The service worker cannot read localStorage — mirror it where it can, so
  // the push re-subscribe it performs with no tab open is authenticated too.
  mirrorAuthTokenToSW(token);
}

/** The one reader for the stored JWT. Anything that needs the raw token — a
 *  socket handshake, a top-level navigation — goes through this rather than
 *  re-typing the storage key. */
export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/**
 * Append the JWT to a URL as `?_t=` — for TOP-LEVEL NAVIGATIONS ONLY.
 *
 * A navigation cannot carry a header, and the cookie is not a credential for
 * the users whose aone_sso is over the browser's 4096-byte limit. The two
 * OAuth-start routes (backend routes/metaAdsAuth.js and instagramAuth.js,
 * `scopedAuth`) accept `?_t=` for exactly this reason: their only side effect
 * is a 302 to Meta, and the token leaves the address bar the moment it fires.
 *
 * Do NOT reach for this anywhere else. A token in a URL is a token in browser
 * history, in Referer, and in any log the URL passes through — for documents
 * and data, `openProtectedExport()` or `api` send it in a header instead.
 */
export function withNavAuthToken(url) {
  const token = getAuthToken();
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}_t=${encodeURIComponent(token)}`;
}

export function clearAuthToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* nothing to clear */ }
  clearAuthTokenForSW();   // logout clears both copies, or the SW keeps one
}

// The migration clear is gone with the gating — it would delete the token the
// session depends on.

api.interceptors.request.use(cfg => {
  if (!needsBearerFallback()) return cfg;
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers = { ...cfg.headers, Authorization: `Bearer ${token}` };
  return cfg;
});

// Partner Portal: a login in several companies names the one it is working for
// (utils/partnerAccount.js); only /partner/* calls carry it.
api.interceptors.request.use(cfg => {
  const url = String(cfg.url || '');
  if (!/^\/?partner\//.test(url)) return cfg;
  const acct = activePartnerAccount();
  if (acct) cfg.headers = { ...cfg.headers, 'x-partner-account': acct };
  return cfg;
});

// ── Buyer-portal impersonation (read-only "View as Buyer") ───────────────────
// backend/src/routes/buyerPortal.js buyerOnly lets internal roles view a
// buyer's portal, but ONLY when every /api/buyer/* call carries the client id
// (?_clientId= or x-client-id header). Entry point is Client360's "View as
// Buyer" → /buyer?_clientId=<id>. Buyer pages navigate with bare paths
// (nav('/buyer/cart')) which drop the query string, so the id is seeded from
// the URL once and persisted per-tab in sessionStorage; this interceptor then
// attaches it to buyer-portal calls only. Writes stay blocked server-side
// (isInternalViewer), and the backend ignores the header for real buyer users.
const BUYER_VIEW_KEY = 'aone_buyer_view_client';
export function getBuyerViewClientId() {
  const fromUrl = new URLSearchParams(window.location.search).get('_clientId');
  if (fromUrl) { sessionStorage.setItem(BUYER_VIEW_KEY, fromUrl); return fromUrl; }
  return sessionStorage.getItem(BUYER_VIEW_KEY);
}
export function clearBuyerViewClientId() { sessionStorage.removeItem(BUYER_VIEW_KEY); }
api.interceptors.request.use(cfg => {
  // Matches both '/buyer/...' and the '/api/buyer/...' collision form, since
  // axios runs request interceptors LIFO and the strip-guard may not have run yet.
  if (typeof cfg.url === 'string' && /^(\/api)?\/buyer(\/|\?|$)/.test(cfg.url)) {
    const cid = getBuyerViewClientId();
    if (cid) cfg.headers = { ...cfg.headers, 'x-client-id': cid };
  }
  return cfg;
});

// ── Safety net: strip baseURL collision from request URLs ────────────────────
// A recurring bug in this codebase: a page calls api.get('/api/foo') even
// though baseURL is already '/api', producing a `/api/api/foo` request. That
// path doesn't exist on the server, so the SPA fallback returns `index.html`
// as the response body. Downstream code like
//     const { data } = await api.get('/api/foo');
//     setRows(data); rows.find(...)
// then crashes at first render with "find/filter/map is not a function" —
// the state holds an HTML string, not an array. We caught four such crashes
// in a single day (Aira, DDGapAudit, GoogleAds, ...) before adding this.
//
// The guard auto-strips the duplicate so the request reaches the real
// endpoint, and console.warn's so the offending caller is visible in
// DevTools. The console warning is the teaching signal — the real fix is
// still to clean up the call site (this is a net, not a cure).
//
// FUTURE TIGHTENING (commented escalation path):
//   In dev (NODE_ENV !== 'production') you could throw here instead of
//   warning, to force the offending caller to be fixed before merge.
//   Today it warns in all envs — pragmatic given how often new pages land.
function _stripBaseUrlCollision(url, baseURL) {
  if (!url || typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url)) return url; // absolute URLs override baseURL
  let basePath;
  try { basePath = new URL(baseURL, 'http://_').pathname.replace(/\/$/, ''); }
  catch { basePath = String(baseURL || '').replace(/^https?:\/\/[^/]+/, '').replace(/\/$/, ''); }
  if (!basePath || basePath === '/') return url;
  if (url === basePath || url.startsWith(basePath + '/')) {
    const stripped = url.slice(basePath.length) || '/';
    // Always-on warning (cheap — only logs on actual collision).
    // eslint-disable-next-line no-console
    console.warn(
      `[api] stripped duplicate baseURL "${basePath}" from "${url}" → "${stripped}". ` +
      `Fix the caller to use "${stripped}".`
    );
    // To escalate to a hard failure in dev, uncomment:
    //   if (process.env.NODE_ENV !== 'production') {
    //     throw new Error(`[api] baseURL collision: pass "${stripped}" not "${url}"`);
    //   }
    return stripped;
  }
  return url;
}

api.interceptors.request.use(cfg => {
  const base = cfg.baseURL ?? api.defaults.baseURL;
  cfg.url = _stripBaseUrlCollision(cfg.url, base);
  return cfg;
});

// Only redirect to /login when we're certain the 401 is OUR session expiring,
// not an upstream provider 401 leaking through (which used to log users out
// of AONE every time the AI ✦ button was clicked with a missing API key).
// "Real" auth failures come from /auth/* endpoints or carry our own error
// shape ("No token provided" / "Invalid token") set by middleware/auth.js.
const SESSION_ERROR_TEXTS = ['No token provided', 'Invalid token', 'Token expired', 'signed in somewhere else'];
// There are TWO response interceptors here — one on the `api` instance and one
// on global axios for the ~500 call sites that import axios directly — and both
// send an expired session to /login. Guarding one and missing the other is why
// this took two passes: the component gallery kept navigating the whole tab to
// the login screen, which is how SalesOrdersDesktop went its entire life
// without once being looked at. One function, called from both.
//
// The gallery stubs the network and has no session to expire, so a redirect
// from it is always a harness artefact. Say so rather than acting on it.
function goToLogin(err) {
  if (typeof window === 'undefined') return;
  if (window.__AONE_PREVIEW__) {
    console.warn('[preview] suppressed /login redirect from', err?.config?.url, err?.response?.data);
    return;
  }
  window.location.href = loginPath();
}

function isOurSessionExpiry(err) {
  const url = err.config?.url || '';
  if (url === '/auth/me') return false; // explicit "am I logged in?" probe
  // force-reset-password uses a short-lived resetToken (not a session token).
  // A 401 here means the reset token expired — let the component handle it and
  // show the error rather than silently redirecting and losing the user's context.
  if (url === '/auth/force-reset-password') return false;
  if (url.startsWith('/auth/')) return true;
  if (err.response?.data?.code === 'SESSION_SUPERSEDED') return true;
  // Account switched off while signed in (middleware/auth.js deactivated gate) —
  // send them to /login rather than leaving a dead screen behind 401s.
  if (err.response?.data?.code === 'ACCOUNT_INACTIVE') return true;
  const msg = err.response?.data?.error || '';
  return SESSION_ERROR_TEXTS.some((s) => msg.includes(s));
}

// Auto-heal a stale token: a freshly-granted module/permission won't be in the
// client's cached JWT until the token is refreshed. On a module/permission 403,
// refresh once via /auth/me (re-derives modules from DB + issues a new token),
// persist it, and retry the original request — so grants take effect WITHOUT a
// manual reload or re-login. Guarded to retry at most once per request.
function isModulePermission403(err) {
  if (err.response?.status !== 403) return false;
  const d = err.response?.data || {};
  return Boolean(d.requiredModule || d.requiredPermission ||
    (typeof d.error === 'string' && d.error.startsWith('Access denied')));
}

// ── Offline mode ─────────────────────────────────────────────────────────────
// Whitelisted GETs snapshot to localStorage on success; when a request never
// reaches the server, the snapshot is served back with `response.offline=true`
// (and `offlineAt`) so screens show last-known data instead of an error card.
api.interceptors.response.use(r => {
  if ((r.config?.method || 'get').toLowerCase() === 'get' && isCacheable(r.config?.url)) {
    cacheSet(r.config.url, r.data);
  }
  return r;
}, err => Promise.reject(err));

api.interceptors.response.use(r => r, async err => {
  const cfg = err.config || {};
  if ((cfg.method || 'get').toLowerCase() === 'get' && isCacheable(cfg.url) && isNetworkError(err)) {
    const hit = cacheGet(cfg.url);
    if (hit) {
      return { data: hit.data, status: 200, statusText: 'OK (offline cache)', headers: {}, config: cfg, offline: true, offlineAt: hit.at };
    }
  }
  return Promise.reject(err);
});

api.interceptors.response.use(r => r, async err => {
  const cfg = err.config || {};
  if (isModulePermission403(err) && !cfg.__retriedAfterRefresh && cfg.url !== '/auth/me') {
    cfg.__retriedAfterRefresh = true;
    try {
      const me = await api.get('/auth/me');
      if (me.data?.token) storeAuthToken(me.data.token);
      return api(cfg); // request interceptor re-reads the refreshed Bearer token
    } catch (_) { /* fall through to normal handling */ }
  }
  if (err.response?.status === 401 && isOurSessionExpiry(err)) {
    goToLogin(err);
  }
  return Promise.reject(err);
});

// ── Global axios hardening ───────────────────────────────────────────────────
// Many pages still `import axios from 'axios'` and call `${API}/api/...` directly
// (cookie-only — no Bearer header, no auto-heal). Patch the GLOBAL axios default
// so those calls ALSO carry the refreshed token and self-heal on a module/
// permission 403, matching the `api` instance. Host-guarded: only attach our JWT
// to our own URLs (relative, this page's host, or the host of REACT_APP_API_URL /
// REACT_APP_PUBLIC_APP_URL) — never leak it to external services (S3, third-party APIs).
function ownHosts() {
  const hosts = new Set();
  if (typeof window !== 'undefined' && window.location?.hostname) hosts.add(window.location.hostname.toLowerCase());
  for (const u of [process.env.REACT_APP_API_URL, process.env.REACT_APP_PUBLIC_APP_URL]) {
    try { if (u && /^https?:\/\//i.test(u)) hosts.add(new URL(u).hostname.toLowerCase()); } catch { /* not a URL */ }
  }
  return hosts;
}
function isAoneUrl(url) {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return true; // relative → our API
  try { return ownHosts().has(new URL(url).hostname.toLowerCase()); }
  catch { return false; }
}
axios.defaults.withCredentials = true;
// The `api` instance has had a 20s timeout since day one, but the ~500 call
// sites that `import axios from 'axios'` directly inherited axios's default of
// NO timeout: a stalled backend left those requests pending forever, and the
// page's spinner with them. 120s rather than 20s because this path also
// carries the slow work — AI generation, file parsing, CSV imports — where a
// 20s ceiling would turn working features into failures. The goal here is a
// bound, not a tight one; anything that fails to answer in two minutes was
// never going to.
axios.defaults.timeout = 120000;
axios.interceptors.request.use(cfg => {
  // File uploads are exempt: a large multipart body on a slow connection can
  // legitimately exceed any fixed ceiling, and the user is watching a real
  // progress bar rather than an unexplained spinner. Explicit per-call
  // timeouts (cfg.timeout set by the caller) always win.
  const isUpload = typeof FormData !== 'undefined' && cfg.data instanceof FormData;
  if (isUpload && cfg.timeout === axios.defaults.timeout) cfg.timeout = 0;
  return cfg;
});
// The bearer attach and the 403 self-heal, as named functions, because three
// kinds of consumer need exactly the same behaviour: the `api` instance, the
// global axios default, and any client a page builds with `axios.create()`.
//
// INCIDENT 2026-09-01 — a created instance inherits axios's *defaults* but NOT
// its interceptors, so `const ax = () => axios.create({ baseURL: API })` inside
// a page went out cookie-only, no bearer, no self-heal. That is invisible right
// up until it isn't: the six widest-access users (one of them an admin) have an
// `aone_sso` cookie past the browser's 4096-byte limit, so the browser drops it
// and the bearer is their ONLY credential — see [[jwt-cookie-size-limit]]. The
// desktop /samples list (page-local client) read "0 total / no samples match"
// for them while the mobile list, on the same session, showed 200 rows, because
// MobileSamples goes through `api`. Pages must build clients with
// `createAuthedClient` below; `api.test.js` fails the build on a bare
// `axios.create(` anywhere else in src/.
export function attachAoneAuth(cfg) {
  // Same rule as the `api` instance: the token only exists, and is only sent,
  // where the httpOnly cookie cannot reach. Reading localStorage here regardless
  // would leave the credential in play on the same-origin deployment even after
  // the instance stopped using it — the hole half-closed is still a hole.
  if (needsBearerFallback() && isAoneUrl(cfg.url)) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) cfg.headers = { ...cfg.headers, Authorization: `Bearer ${token}` };
  }
  return cfg;
}

/** Retry once with a refreshed token on a module/permission 403, then the
 *  session-expiry redirect. `instance` replays the original request. */
function aoneAuthErrorHandler(instance) {
  return async (err) => {
    const cfg = err.config || {};
    if (isModulePermission403(err) && isAoneUrl(cfg.url) && !cfg.__retriedAfterRefresh) {
      cfg.__retriedAfterRefresh = true;
      try {
        const me = await api.get('/auth/me');
        if (me.data?.token) storeAuthToken(me.data.token);
        return instance(cfg); // the request interceptor re-reads the refreshed token
      } catch (_) { /* fall through */ }
    }
    if (err.response?.status === 401 && isAoneUrl(cfg.url) && isOurSessionExpiry(err)) {
      goToLogin(err);
    }
    return Promise.reject(err);
  };
}

/**
 * An axios instance that authenticates like `api` does.
 *
 * For the handful of places that genuinely need their own client — a different
 * baseURL, or a tighter timeout than the global 120s ceiling — instead of
 * `axios.create()`, which produces a client with no credentials attached.
 */
export function createAuthedClient(config = {}) {
  const instance = axios.create({ withCredentials: true, ...config });
  instance.interceptors.request.use(attachAoneAuth);
  instance.interceptors.response.use(r => r, aoneAuthErrorHandler(instance));
  return instance;
}

axios.interceptors.request.use(attachAoneAuth);
axios.interceptors.response.use(r => r, aoneAuthErrorHandler(axios));

// ── Global fetch hardening ───────────────────────────────────────────────────
// 28 screens skip axios entirely and call window.fetch with
// `credentials: 'include'`, which is the same blind spot one layer down: a
// cookie the browser refused to store is not a credential, and those screens go
// blank for exactly the users the bearer token exists to carry. Wrapping fetch
// once beats rewriting several hundred call sites, and it keeps the host guard
// in a single place. Exported so the wrapper is testable without reaching for
// the real network.
export function withAoneAuth(nativeFetch) {
  const wrapped = (input, init) => {
    const url = typeof input === 'string' ? input : (input?.url || '');
    if (!needsBearerFallback() || !isAoneUrl(url)) return nativeFetch(input, init);
    let token = null;
    try { token = localStorage.getItem(TOKEN_KEY); } catch { /* storage blocked */ }
    if (!token) return nativeFetch(input, init);
    const headers = new Headers(init?.headers ?? (typeof input === 'object' ? input?.headers : undefined));
    // A caller that set its own Authorization (a vendor/buyer token) wins.
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return nativeFetch(input, { credentials: 'include', ...init, headers });
  };
  wrapped.__aoneAuthed = true;
  return wrapped;
}

if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.fetch.__aoneAuthed) {
  window.fetch = withAoneAuth(window.fetch.bind(window));
}

// Reconnect: replay queued offline writes (approval decisions) once, then let
// the interested screens refetch on their own focus/interval logic.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    replayQueue(api).then(({ sent, dropped }) => {
      if (sent || dropped) window.dispatchEvent(new CustomEvent('aone:offline-replayed', { detail: { sent, dropped } }));
    }).catch(() => {});
  });
}

export default api;
